import { Capacitor } from "@capacitor/core";
import {
  LOG_LEVEL,
  PRODUCT_CATEGORY,
  PURCHASES_ERROR_CODE,
  Purchases,
  type CustomerInfo,
  type PurchasesError,
  type PurchasesOffering,
  type PurchasesPackage,
  type PurchasesStoreProduct,
} from "@revenuecat/purchases-capacitor";

import { revenueCatAndroidKey, revenueCatAppleKey, revenueCatEntitlementId } from "@/lib/env";

export type PlanId = "yearly" | "lifetime";

/** Trial metadata read off the `premium` entitlement right after a successful purchase. */
export type TrialInfo = {
  trialEndsAtMs: number;
};

/**
 * Outcome of a native purchase attempt.
 *
 * - `success`     — purchase completed and the `premium` entitlement is active.
 *                   `trial` is set when the entitlement is currently in its free-trial
 *                   period (`periodType === "TRIAL"`); `null` for an immediate charge
 *                   (e.g. lifetime, or a yearly purchase with no intro trial).
 * - `cancelled`   — the user dismissed the StoreKit/Play sheet. No error UI.
 * - `not_entitled`— purchase resolved but `premium` is not active yet. Do not advance.
 * - `unsupported` — non-native or no API key for this platform. Caller decides.
 * - `error`       — anything else (offering/package missing, store/network failure).
 */
export type PurchaseOutcome =
  | { status: "success"; trial: TrialInfo | null }
  | { status: "cancelled" }
  | { status: "not_entitled" }
  | { status: "unsupported" }
  | { status: "error"; reason: string };

/**
 * Outcome of a user-initiated Restore Purchases attempt.
 *
 * - `restored`            — restore ran and the `premium` entitlement is now active.
 * - `nothing_to_restore`  — restore succeeded but no active `premium` entitlement
 *                           exists for this account (nothing to unlock).
 * - `unsupported`         — non-native or no API key for this platform.
 * - `error`               — configure/store/network failure (`reason` for logging).
 */
export type RestoreOutcome =
  | { status: "restored" }
  | { status: "nothing_to_restore" }
  | { status: "unsupported" }
  | { status: "error"; reason: string };

/**
 * A resolved purchasable for one plan. `aPackage` is set when the plan came from
 * the offering (preferred path); it is `null` when we fell back to a bare store
 * product via `getProducts`. Either way `product` carries the fields the paywall
 * renders (priceString, introPrice, pricePerMonthString, …).
 */
export type PlanCatalogEntry = {
  product: PurchasesStoreProduct;
  aPackage: PurchasesPackage | null;
};

export type PaywallCatalog = {
  yearly: PlanCatalogEntry;
  lifetime: PlanCatalogEntry;
};

const RC_ANNUAL_PACKAGE_ID = "$rc_annual";
const RC_LIFETIME_PACKAGE_ID = "$rc_lifetime";
const RC_DEFAULT_OFFERING_ID = "default";

// StoreKit product identifiers, used only by the `getProducts` fallback when the
// offering does not yield a package. Annual is a subscription; lifetime is a
// one-time (non-subscription) product — each must be fetched with its own
// category or StoreKit returns nothing.
const IOS_YEARLY_PRODUCT_ID = "com.screen0.premium.yearly";
const IOS_LIFETIME_PRODUCT_ID = "com.screen0.premium.lifetime";

// RevenueCat must be configured exactly once per app session. The cached promise
// is cleared on failure so a later mount can retry.
let configurePromise: Promise<boolean> | null = null;
let configuredAppUserId: string | null = null;

// Reason for the most recent failed `configurePurchases`, surfaced in the paywall
// so a configure/bridge failure is diagnosable from the device itself (TestFlight
// users may have no Mac / device-log access). `null` once a configure succeeds.
let lastConfigureFailure: string | null = null;

/** Short reason for the most recent failed `configurePurchases`, or `null`. */
export function getConfigureFailureReason(): string | null {
  return lastConfigureFailure;
}

function platformApiKey(): string | null {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return revenueCatAppleKey || null;
  if (platform === "android") return revenueCatAndroidKey || null;
  return null;
}

/**
 * Native platform with the `Purchases` plugin actually linked into the build and
 * a configured public SDK key. Gating on `isPluginAvailable` (not just
 * `isNativePlatform`) is what stops JS calls from hanging when the native pod is
 * missing from the archive — the classic cause of a "stuck" paywall.
 */
export function isPurchasesSupported(): boolean {
  return (
    Capacitor.isNativePlatform() &&
    Capacitor.isPluginAvailable("Purchases") &&
    platformApiKey() !== null
  );
}

function classifyConfigureError(error: unknown): string {
  if (error && typeof error === "object") {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message) {
      if (/not implemented|unimplemented/i.test(message)) return "plugin_unavailable";
      return message.slice(0, 80);
    }
  }
  return "unknown";
}

/** Identifies the RevenueCat user, re-logging in only when the id changes. */
async function identify(appUserID: string): Promise<void> {
  if (!appUserID || configuredAppUserId === appUserID) return;
  await Purchases.logIn({ appUserID });
  configuredAppUserId = appUserID;
}

/**
 * Configures RevenueCat with the platform public SDK key, then identifies the
 * anonymous device id via `logIn` (no `appUserID` in `configure`). Native-only
 * and idempotent — safe to call at startup and again before a purchase. Resolves
 * `false` when unsupported. There is intentionally NO artificial timeout: on a
 * correctly linked build the native calls resolve on their own; a hard timeout
 * only converts slow StoreKit/TestFlight hydration into a false failure.
 */
export function configurePurchases(appUserID: string): Promise<boolean> {
  if (!isPurchasesSupported()) {
    if (Capacitor.isNativePlatform()) {
      lastConfigureFailure = Capacitor.isPluginAvailable("Purchases")
        ? "no_api_key"
        : "plugin_unavailable";
    }
    return Promise.resolve(false);
  }

  // Already configuring/configured this session: re-identify if the user changed.
  if (configurePromise) {
    if (configuredAppUserId === appUserID) return configurePromise;
    return configurePromise.then(async (ok) => {
      if (ok) {
        try {
          await identify(appUserID);
        } catch (error) {
          console.warn("[revenuecat] re-identify failed", error);
        }
      }
      return ok;
    });
  }

  const apiKey = platformApiKey();
  if (!apiKey) {
    lastConfigureFailure = "no_api_key";
    return Promise.resolve(false);
  }

  configurePromise = (async () => {
    try {
      // DEBUG logs are console-only (Xcode/device console) and never user-visible;
      // they are the primary way to diagnose the purchase path on TestFlight.
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      const { isConfigured } = await Purchases.isConfigured();
      if (!isConfigured) {
        await Purchases.configure({ apiKey });
      }
      await identify(appUserID);
      lastConfigureFailure = null;
      return true;
    } catch (error) {
      lastConfigureFailure = classifyConfigureError(error);
      console.error(`[revenuecat] configure failed (${lastConfigureFailure})`, error);
      configurePromise = null;
      configuredAppUserId = null;
      return false;
    }
  })();

  return configurePromise;
}

/** Loads the current (`default`) offering, or `null` when none/unavailable. */
async function loadOffering(): Promise<PurchasesOffering | null> {
  try {
    const offerings = await Purchases.getOfferings();
    const offering = offerings.current ?? offerings.all[RC_DEFAULT_OFFERING_ID] ?? null;
    if (!offering) {
      console.warn(
        `[revenuecat] no current offering; available: [${Object.keys(offerings.all ?? {}).join(", ") || "none"}]`,
      );
      return null;
    }
    console.info(
      `[revenuecat] offering "${offering.identifier}" (${offering.availablePackages.length} packages: ` +
        `[${offering.availablePackages.map((pkg) => pkg.identifier).join(", ") || "none"}])`,
    );
    return offering;
  } catch (error) {
    console.warn("[revenuecat] getOfferings failed; falling back to getProducts", error);
    return null;
  }
}

function packageFromOffering(
  offering: PurchasesOffering | null,
  plan: PlanId,
): PurchasesPackage | null {
  if (!offering) return null;
  if (plan === "yearly") {
    return (
      offering.annual ??
      offering.availablePackages.find((pkg) => pkg.identifier === RC_ANNUAL_PACKAGE_ID) ??
      null
    );
  }
  return (
    offering.lifetime ??
    offering.availablePackages.find((pkg) => pkg.identifier === RC_LIFETIME_PACKAGE_ID) ??
    null
  );
}

/**
 * Resolves one plan to a purchasable: the offering package when present,
 * otherwise the bare store product fetched with its correct category. The
 * product fallback is iOS-only — the Android store ids differ and the Android
 * key is known-missing, so Android stays on the offering path.
 */
async function resolvePlanEntry(
  plan: PlanId,
  offering: PurchasesOffering | null,
): Promise<PlanCatalogEntry | null> {
  const pkg = packageFromOffering(offering, plan);
  if (pkg) {
    console.info(
      `[revenuecat] ${plan}: package "${pkg.identifier}" product "${pkg.product.identifier}" price="${pkg.product.priceString}"`,
    );
    return { product: pkg.product, aPackage: pkg };
  }

  if (Capacitor.getPlatform() !== "ios") return null;

  const productId = plan === "yearly" ? IOS_YEARLY_PRODUCT_ID : IOS_LIFETIME_PRODUCT_ID;
  const type =
    plan === "yearly" ? PRODUCT_CATEGORY.SUBSCRIPTION : PRODUCT_CATEGORY.NON_SUBSCRIPTION;

  try {
    const { products } = await Purchases.getProducts({ productIdentifiers: [productId], type });
    const product = products.find((item) => item.identifier === productId) ?? products[0] ?? null;
    if (product) {
      console.info(
        `[revenuecat] ${plan}: getProducts "${product.identifier}" price="${product.priceString}"`,
      );
      return { product, aPackage: null };
    }
    console.warn(`[revenuecat] ${plan}: product "${productId}" not returned by getProducts`);
  } catch (error) {
    console.warn(`[revenuecat] ${plan}: getProducts failed for "${productId}"`, error);
  }
  return null;
}

/** Resolves yearly + lifetime purchasables for the paywall, or `null`. */
export async function getPaywallCatalog(): Promise<PaywallCatalog | null> {
  if (!isPurchasesSupported()) return null;

  const offering = await loadOffering();
  const [yearly, lifetime] = await Promise.all([
    resolvePlanEntry("yearly", offering),
    resolvePlanEntry("lifetime", offering),
  ]);

  if (!yearly || !lifetime) {
    console.warn(
      `[revenuecat] incomplete catalog: yearly=${Boolean(yearly)} lifetime=${Boolean(lifetime)} ` +
        `(expected ${RC_ANNUAL_PACKAGE_ID} + ${RC_LIFETIME_PACKAGE_ID}).`,
    );
    return null;
  }

  // Packages can resolve before StoreKit/ASC has hydrated prices. Log but still
  // return them so the purchase path stays available.
  if (!yearly.product.priceString || !lifetime.product.priceString) {
    console.warn(
      "[revenuecat] missing priceString (StoreKit/ASC product not ready). " +
        `yearly="${yearly.product.priceString}" lifetime="${lifetime.product.priceString}".`,
    );
  }

  return { yearly, lifetime };
}

function toPurchasesError(error: unknown): Partial<PurchasesError> | null {
  if (!error || typeof error !== "object") return null;
  return error as Partial<PurchasesError>;
}

function isUserCancelled(error: unknown): boolean {
  const purchasesError = toPurchasesError(error);
  return (
    purchasesError?.userCancelled === true ||
    purchasesError?.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR
  );
}

function errorReason(error: unknown): string {
  const message = toPurchasesError(error)?.message;
  if (typeof message === "string" && message) return message;
  return "purchase_failed";
}

/**
 * Configures (if needed), resolves the purchasable for `plan`, and runs the
 * native purchase via package (preferred) or bare store product (fallback).
 * Success requires the `premium` entitlement to be active afterwards.
 */
export async function purchaseSelectedPlan(
  plan: PlanId,
  appUserID: string,
): Promise<PurchaseOutcome> {
  if (!isPurchasesSupported()) return { status: "unsupported" };

  const configured = await configurePurchases(appUserID);
  if (!configured) return { status: "error", reason: "not_configured" };

  try {
    const offering = await loadOffering();
    const entry = await resolvePlanEntry(plan, offering);
    if (!entry) return { status: "error", reason: "package_not_found" };

    const result = entry.aPackage
      ? await Purchases.purchasePackage({ aPackage: entry.aPackage })
      : await Purchases.purchaseStoreProduct({ product: entry.product });

    const active = result.customerInfo.entitlements.active[revenueCatEntitlementId];
    if (!active) return { status: "not_entitled" };

    const trial =
      active.periodType === "TRIAL" && active.expirationDateMillis
        ? { trialEndsAtMs: active.expirationDateMillis }
        : null;
    return { status: "success", trial };
  } catch (error) {
    if (isUserCancelled(error)) return { status: "cancelled" };
    console.error("[revenuecat] purchase failed", error);
    return { status: "error", reason: errorReason(error) };
  }
}

/** True when the customer info carries an active `premium` entitlement. */
export function customerInfoHasPremium(customerInfo: CustomerInfo): boolean {
  return Boolean(customerInfo.entitlements.active[revenueCatEntitlementId]);
}

/** Reads current entitlement state from RevenueCat (native only). */
export async function getPremiumStatus(appUserID: string): Promise<{ isPremium: boolean } | null> {
  if (!isPurchasesSupported()) return null;

  const configured = await configurePurchases(appUserID);
  if (!configured) return null;

  try {
    const { customerInfo } = await Purchases.getCustomerInfo();
    return { isPremium: customerInfoHasPremium(customerInfo) };
  } catch (error) {
    console.warn("[revenuecat] getCustomerInfo failed", error);
    return null;
  }
}

/** Refreshes CustomerInfo from RevenueCat after restore (promotional grants, etc.). */
async function fetchFreshCustomerInfo(appUserID: string): Promise<CustomerInfo> {
  try {
    await Purchases.invalidateCustomerInfoCache();
  } catch (error) {
    console.warn(`[revenuecat] invalidateCustomerInfoCache failed user=${appUserID}`, error);
  }
  const { customerInfo } = await Purchases.getCustomerInfo();
  return customerInfo;
}

/**
 * User-initiated Restore Purchases (Guideline 3.1.1). Configures (if needed) and
 * runs RevenueCat's native `restorePurchases`, then reports whether the `premium`
 * entitlement is active for the restoring account. Must only be called from an
 * explicit user action (a Restore button) — never on launch or in
 * `configurePurchases`.
 */
export async function restorePurchases(appUserID: string): Promise<RestoreOutcome> {
  if (!isPurchasesSupported()) return { status: "unsupported" };

  const configured = await configurePurchases(appUserID);
  if (!configured) return { status: "error", reason: "not_configured" };

  try {
    console.info(`[revenuecat] restore start user=${appUserID}`);

    const { customerInfo: restoreInfo } = await Purchases.restorePurchases();
    const restoreHasPremium = customerInfoHasPremium(restoreInfo);
    console.info(
      `[revenuecat] restore after restorePurchases user=${appUserID} premium=${restoreHasPremium}`,
    );

    // Promotional/server-side grants are tied to app_user_id and may not appear
    // in the restorePurchases response alone (especially on Android Play Billing).
    const customerInfo = await fetchFreshCustomerInfo(appUserID);
    const hasPremium = customerInfoHasPremium(customerInfo);
    console.info(`[revenuecat] restore final user=${appUserID} premium=${hasPremium}`);

    return hasPremium ? { status: "restored" } : { status: "nothing_to_restore" };
  } catch (error) {
    console.error(`[revenuecat] restore failed user=${appUserID}`, error);
    return { status: "error", reason: errorReason(error) };
  }
}
